import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Search, SlidersHorizontal, X, Bookmark, BookmarkCheck, ArrowUpRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import InternalAd from "@/components/InternalAd";
import { useAuth } from "@/context/AuthContext";
import { shareJob } from "@/lib/share";
import { getLocationBadge } from "@/lib/countryBadge";

// Quick-filter chip rails — one-tap popular filters shown under the sticky header.
// Each chip mimics filling the corresponding filter (location / sector / q).
const QUICK_CHIP_GROUPS = [
  {
    kind: "country", label: "Region",
    chips: [
      { label: "India",          value: "India" },
      { label: "United States",  value: "United States" },
      { label: "United Kingdom", value: "United Kingdom" },
      { label: "Europe",         value: "Europe" },
      { label: "Singapore",      value: "Singapore" },
      { label: "Australia",      value: "Australia" },
    ],
  },
  {
    kind: "role", label: "AI Roles",
    chips: [
      { label: "AI",               value: "AI" },
      { label: "AI Engineer",      value: "AI Engineer" },
      { label: "MLOps",            value: "MLOps" },
      { label: "AIML",             value: "AIML" },
      { label: "Machine Learning", value: "Machine Learning" },
    ],
  },
  {
    // Order reflects live job counts for each keyword (descending) as of the
    // 2026-08 corpus-widening pass — re-check counts if the corpus shifts a lot.
    kind: "top_role", label: "Top Roles",
    chips: [
      { label: "Data",       value: "Data" },
      { label: "Product",    value: "Product" },
      { label: "Cloud",      value: "Cloud" },
      { label: "Java",       value: "Java" },
      { label: "Python",     value: "Python" },
      { label: "Full Stack", value: "Full Stack" },
      { label: "DevOps",     value: "DevOps" },
    ],
  },
  {
    kind: "sector", label: "Sectors",
    chips: [
      { label: "Technology",  value: "Technology" },
      { label: "BFSI",        value: "BFSI" },
      { label: "Retail",      value: "Retail" },
      { label: "Consulting",  value: "Consulting" },
      { label: "Pharma",      value: "Pharma" },
      { label: "Healthcare",  value: "Healthcare" },
    ],
  },
];


// ─── Editorial job row — Tangle-style, list with horizontal divider only ───
const JobRow = ({ job, saved, onSave }) => {
  const domain =
    job.company_domain ||
    (job.company || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 24) + ".com";
  const badge = getLocationBadge(job);
  return (
    <article
      data-testid={`job-card-${job.job_id}`}
      className="group py-4 md:py-6 border-b transition-colors duration-200"
      style={{ borderColor: "var(--karm-paper-edge)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--karm-ivory-warm)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div className="flex items-start gap-3 md:gap-5 px-2">
        <img
          src={`https://logo.clearbit.com/${domain}`}
          alt=""
          loading="lazy"
          className="h-8 w-8 md:h-10 md:w-10 rounded-sm object-contain mt-1 grayscale group-hover:grayscale-0 transition"
          onError={(e) => (e.currentTarget.style.visibility = "hidden")}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 mb-0.5 md:mb-1 flex-wrap">
            <span className="small-caps">{job.company}</span>
            {job.sector && (
              <span
                className="text-[10px] px-2 py-0.5 lowercase"
                style={{ background: "var(--karm-ivory-warm)", color: "var(--karm-ink-muted)" }}
              >
                {job.sector}
              </span>
            )}
          </div>
          <Link
            to={`/jobs/${job.job_id}`}
            className="font-serif text-base md:text-2xl leading-snug block mb-0.5 md:mb-1 group-hover:text-[color:var(--karm-vermillion)]"
            style={{ color: "var(--karm-ink)" }}
            data-testid={`job-title-link-${job.job_id}`}
          >
            {job.title}
          </Link>
          <p className="small-caps">
            {badge && (
              <span className="mr-1.5" title={badge.label} data-testid={`country-badge-${job.job_id}`}>
                <span aria-hidden="true">{badge.flag}</span> {badge.label}
              </span>
            )}
            {job.location}
            {job.posted_age && <span className="opacity-60"> · {job.posted_age}</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => shareJob(job)}
              data-testid={`job-share-${job.job_id}`}
              aria-label="Share"
              className="transition-opacity hover:opacity-100 opacity-50"
              style={{ color: "var(--karm-ink-muted)" }}
            >
              <Share2 className="h-4 w-4" />
            </button>
            {onSave && (
              <button
                onClick={() => onSave(job.job_id, !saved)}
                data-testid={`job-save-${job.job_id}`}
                aria-label={saved ? "Unsave" : "Save"}
                className="transition-opacity hover:opacity-100 opacity-50"
                style={{ color: "var(--karm-ink-muted)" }}
              >
                {saved ? (
                  <BookmarkCheck className="h-4 w-4" style={{ color: "var(--karm-vermillion)" }} />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
          <Link
            to={`/jobs/${job.job_id}`}
            className="font-serif italic text-sm flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100"
            style={{ color: "var(--karm-vermillion)" }}
            data-testid={`job-apply-link-${job.job_id}`}
          >
            Read more <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </article>
  );
};

// ─── Filter group block (left rail) ───
const FilterBlock = ({ title, children, testId }) => (
  <div className="mb-8" data-testid={testId}>
    <p className="small-caps mb-3">{title}</p>
    {children}
  </div>
);

export default function JobsPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [location, setLocation] = useState(params.get("location") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const [sector, setSector] = useState(params.get("sector") || "");
  const [company, setCompany] = useState(params.get("company") || "");
  // Dynamic per-search SEO title — "Program Manager GCC jobs in Pune | KARM"
  const seoTitle = useMemo(() => {
    const filters = [q, sector, company, location || country].filter(Boolean).join(" · ");
    return filters
      ? `${filters} — GCC & Global Jobs | KARM`
      : "Search Global GCC Jobs — Live Roles Worldwide | KARM";
  }, [q, sector, company, location, country]);
  const seoDesc = useMemo(() => {
    const filters = [q, sector, company, location || country].filter(Boolean).join(", ");
    return filters
      ? `Search open roles for ${filters} at Global Capability Centres worldwide.`
      : "Search curated roles at Global Capability Centres worldwide — India, US, UK, Europe, Singapore, Australia, and more.";
  }, [q, sector, company, location, country]);
  useSEO({ title: seoTitle, description: seoDesc, path: "/jobs" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savedSet, setSavedSet] = useState(new Set());
  const { user } = useAuth();
  const [sectors, setSectors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Pagination
  const [pageSize, setPageSize] = useState(Number(params.get("size")) || 25);
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchJobs = async (overrides) => {
    setLoading(true);
    const size = overrides?.pageSize ?? pageSize;
    const pg = overrides?.page ?? page;
    const payload = {
      q: overrides?.q ?? q,
      location: overrides?.location ?? location,
      country: overrides?.country ?? country,
      sector: overrides?.sector ?? sector,
      company: overrides?.company ?? company,
      limit: size,
      skip: (pg - 1) * size,
    };
    try {
      const r = await api.get("/jobs", { params: payload });
      const list = r.data.items || [];
      setItems(list);
      setTotal(r.data.total || 0);
    } catch { /* swallow — anti-scraping 429 shouldn't surface a CRA red overlay */ }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Intentional one-time fetch using the initial URL-derived filter state;
    // every subsequent fetch (filters, pagination) is triggered explicitly
    // by applyFilters/goToPage/changePageSize below with fresh overrides, so
    // re-running this on every fetchJobs identity change would double-fetch.
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/companies/sectors").then((r) => setSectors(r.data || [])).catch(() => {});
    api.get("/companies/with-jobs").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/saved-jobs/me").then((r) => setSavedSet(new Set((r.data.items || []).map((i) => i.job_id)))).catch(() => {});
  }, []);

  // Re-fetch whenever search bar submits OR filter side-rail toggles.
  // Always reset to page 1 — otherwise a user on page 8 who narrows filters
  // ends up on an empty page.
  const applyFilters = (next) => {
    const merged = {
      q: next.q ?? q,
      location: next.location ?? location,
      country: next.country ?? country,
      sector: next.sector ?? sector,
      company: next.company ?? company,
    };
    // Sync local state so chips + inputs mirror the actual active filters.
    // Without this, clicking × on a chip leaves the chip on screen even though
    // the URL + fetch have already dropped it.
    setQ(merged.q);
    setLocation(merged.location);
    setCountry(merged.country);
    setSector(merged.sector);
    setCompany(merged.company);
    const urlParams = {};
    Object.entries(merged).forEach(([k, v]) => v && (urlParams[k] = v));
    if (pageSize !== 25) urlParams.size = String(pageSize);
    setParams(urlParams);
    setPage(1);
    fetchJobs({ ...merged, page: 1 });
    setMobileFiltersOpen(false);
  };

  const goToPage = (nextPage) => {
    const target = Math.min(Math.max(1, nextPage), totalPages);
    if (target === page) return;
    setPage(target);
    // Preserve current URL params but bump page.
    const urlParams = Object.fromEntries(params.entries());
    urlParams.page = String(target);
    if (pageSize !== 25) urlParams.size = String(pageSize);
    else delete urlParams.size;
    setParams(urlParams);
    fetchJobs({ page: target });
    // Scroll to top of listing so the user sees the new page start.
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changePageSize = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
    const urlParams = Object.fromEntries(params.entries());
    if (nextSize !== 25) urlParams.size = String(nextSize);
    else delete urlParams.size;
    delete urlParams.page;
    setParams(urlParams);
    fetchJobs({ pageSize: nextSize, page: 1 });
  };

  const onSave = async (jobId, willSave) => {
    try {
      if (willSave) {
        await api.post(`/saved-jobs/${jobId}`);
        setSavedSet((s) => new Set([...s, jobId]));
      } else {
        await api.delete(`/saved-jobs/${jobId}`);
        setSavedSet((s) => {
          const n = new Set(s);
          n.delete(jobId);
          return n;
        });
      }
    } catch {
      toast.error("Couldn't update saved jobs — please try again.");
    }
  };

  const filteredCompanies = useMemo(() => {
    if (!companyQuery) return companies.slice(0, 50);
    const q2 = companyQuery.toLowerCase();
    return companies.filter((c) => c.company.toLowerCase().includes(q2)).slice(0, 50);
  }, [companies, companyQuery]);

  const clearFilters = () => {
    setQ("");
    setLocation("");
    setCountry("");
    setSector("");
    setCompany("");
    setParams({});
    fetchJobs({ q: "", location: "", country: "", sector: "", company: "" });
  };

  const activeFilterCount = [q, location, country, sector, company].filter(Boolean).length;

  // ─── Filter rail content (shared between desktop sidebar + mobile bottom sheet) ───
  const FilterRail = (
    <div className="flex flex-col">
      <FilterBlock title="Search" testId="filter-search-block">
        <div className="flex items-center gap-2 border-b pb-2" style={{ borderColor: "var(--karm-paper-edge)" }}>
          <Search className="h-4 w-4" style={{ color: "var(--karm-ink-muted)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters({ q })}
            placeholder="Role, skill, team…"
            className="flex-1 bg-transparent border-0 outline-none py-1 font-sans text-sm"
            style={{ color: "var(--karm-ink)" }}
            data-testid="jobs-search-input"
          />
        </div>
      </FilterBlock>

      <FilterBlock title="Location" testId="filter-location-block">
        <div className="flex items-center gap-2 border-b pb-2 mb-3" style={{ borderColor: "var(--karm-paper-edge)" }}>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters({ location })}
            placeholder="Any city or country…"
            className="flex-1 bg-transparent border-0 outline-none py-1 font-sans text-sm"
            style={{ color: "var(--karm-ink)" }}
            data-testid="filter-location-input"
          />
          {location && (
            <button onClick={() => applyFilters({ location: "" })} aria-label="Clear location" data-testid="filter-location-clear">
              <X className="h-3.5 w-3.5" style={{ color: "var(--karm-ink-muted)" }} />
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {["India", "United States", "United Kingdom", "Europe", "Singapore", "Australia"].map((c) => (
            <button
              key={c}
              onClick={() => applyFilters({ country: country === c ? "" : c })}
              data-testid={`filter-country-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-2 text-left font-sans text-sm w-full py-1 hover:underline underline-offset-4"
              style={{ color: country === c ? "var(--karm-vermillion)" : "var(--karm-ink-muted)" }}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${country === c ? "" : "opacity-30"}`} style={{ background: "var(--karm-vermillion)" }} />
              {c}
            </button>
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Sector" testId="filter-sector-block">
        <div className="space-y-1.5">
          {sectors.slice(0, 10).map((s) => (
            <button
              key={s.sector}
              onClick={() => applyFilters({ sector: sector === s.sector ? "" : s.sector })}
              data-testid={`filter-sector-${s.sector}`}
              className="flex items-center justify-between text-left font-sans text-sm w-full py-1 hover:underline underline-offset-4"
              style={{ color: sector === s.sector ? "var(--karm-vermillion)" : "var(--karm-ink-muted)" }}
            >
              <span className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${sector === s.sector ? "" : "opacity-30"}`} style={{ background: "var(--karm-vermillion)" }} />
                {s.sector}
              </span>
              <span className="opacity-50 text-xs">{s.count}</span>
            </button>
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Company" testId="filter-company-block">
        <input
          value={companyQuery}
          onChange={(e) => setCompanyQuery(e.target.value)}
          placeholder="Type to filter…"
          className="w-full bg-transparent border-b py-1 outline-none font-sans text-sm mb-2"
          style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink)" }}
          data-testid="filter-company-search"
        />
        <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
          {filteredCompanies.map((c) => (
            <button
              key={c.company}
              onClick={() => applyFilters({ company: company === c.company ? "" : c.company })}
              data-testid={`filter-company-${c.company}`}
              className="flex items-center justify-between text-left font-sans text-sm w-full py-1 hover:underline underline-offset-4"
              style={{ color: company === c.company ? "var(--karm-vermillion)" : "var(--karm-ink-muted)" }}
            >
              <span className="truncate">{c.company}</span>
              <span className="opacity-50 text-xs shrink-0 ml-2">{c.count}</span>
            </button>
          ))}
        </div>
      </FilterBlock>

      {activeFilterCount > 0 && (
        <button
          onClick={clearFilters}
          className="small-caps mt-2 hover:underline underline-offset-4 self-start"
          style={{ color: "var(--karm-vermillion)" }}
          data-testid="filter-clear-all"
        >
          ⸻ Clear all {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );

  return (
    <div className="karm-editorial min-h-screen" data-testid="karm-jobs">
      {/* Sticky page header — stacks on mobile so search + filter never get clipped */}
      <div
        className="sticky top-0 z-30 backdrop-blur-sm border-b px-4 md:px-6 py-3 md:py-4"
        style={{ background: "color-mix(in srgb, var(--karm-ivory) 92%, transparent)", borderColor: "var(--karm-paper-edge)" }}
        data-testid="jobs-sticky-header"
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          {/* Line 1 (mobile) / left (desktop): directory title */}
          <p className="font-serif italic text-sm md:text-base shrink-0" style={{ color: "var(--karm-ink)" }}>
            <span className="small-caps not-italic">Master directory ·</span>{" "}
            <span data-testid="jobs-total-count">{total.toLocaleString()}</span> roles
          </p>
          {/* Line 2 (mobile) / center+right (desktop): search + filter side-by-side */}
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <div className="flex-1 flex items-center gap-2 border-b min-w-0" style={{ borderColor: "var(--karm-paper-edge)" }}>
              <Search className="h-4 w-4 shrink-0" style={{ color: "var(--karm-ink-muted)" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters({ q })}
                placeholder={`Search ${total.toLocaleString()} roles…`}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none py-1.5 md:py-2 font-serif text-sm md:text-lg"
                style={{ color: "var(--karm-ink)" }}
                data-testid="jobs-header-search"
              />
              <span className="hidden md:inline small-caps opacity-60">⌘ K</span>
            </div>
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden flex items-center gap-1 small-caps shrink-0"
              style={{ color: "var(--karm-vermillion)" }}
              data-testid="jobs-mobile-filter-btn"
            >
              <SlidersHorizontal className="h-3 w-3" /> Filters
              {activeFilterCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px]"
                  style={{ background: "var(--karm-vermillion)", color: "var(--karm-ivory)" }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Quick-filter chip rails — one-tap popular filters */}
        <div className="max-w-7xl mx-auto mt-3 space-y-2" data-testid="jobs-quick-chips">
          {QUICK_CHIP_GROUPS.map((grp) => (
            <div key={grp.kind} className="flex items-center gap-3 overflow-x-auto no-scrollbar">
              <span
                className="small-caps shrink-0 whitespace-nowrap opacity-60 text-[10px] w-16 md:w-20"
                style={{ color: "var(--karm-ink-muted)" }}
              >
                {grp.label}
              </span>
              <div className="flex gap-1.5 shrink-0">
                {grp.chips.map((c) => {
                  const active =
                    (grp.kind === "country" && country.toLowerCase() === c.value.toLowerCase()) ||
                    (grp.kind === "sector" && sector.toLowerCase() === c.value.toLowerCase()) ||
                    (grp.kind === "role" || grp.kind === "top_role") && q.toLowerCase() === c.value.toLowerCase();
                  const onTap = () => {
                    if (grp.kind === "country") applyFilters({ country: active ? "" : c.value });
                    else if (grp.kind === "sector") applyFilters({ sector: active ? "" : c.value });
                    else applyFilters({ q: active ? "" : c.value });
                  };
                  return (
                    <button
                      key={c.value}
                      onClick={onTap}
                      className="px-2.5 py-1 text-[11px] border rounded-full whitespace-nowrap transition-colors"
                      style={{
                        borderColor: "var(--karm-paper-edge)",
                        color: active ? "var(--karm-ivory)" : "var(--karm-ink-muted)",
                        background: active ? "var(--karm-vermillion)" : "transparent",
                        fontWeight: active ? 600 : 400,
                      }}
                      data-testid={`quick-chip-${grp.kind}-${c.value.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3-column editorial layout */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[280px_1fr] xl:grid-cols-[280px_1fr_260px] gap-0">
        {/* LEFT — filter rail (desktop only) */}
        <aside
          className="hidden lg:block border-r p-6 h-[calc(100vh-73px)] sticky top-[73px] overflow-y-auto"
          style={{ borderColor: "var(--karm-paper-edge)" }}
          data-testid="filter-rail-desktop"
        >
          {FilterRail}
        </aside>

        {/* CENTER — job feed */}
        <main className="px-4 md:px-6 py-4 md:py-8 max-w-3xl w-full" data-testid="jobs-feed">
          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div className="flex items-center flex-wrap gap-2 mb-6">
              {[
                ["q", q],
                ["location", location],
                ["country", country],
                ["sector", sector],
                ["company", company],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => applyFilters({ [k]: "" })}
                    data-testid={`active-filter-${k}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 small-caps hover:opacity-70"
                    style={{ background: "var(--karm-ivory-warm)", color: "var(--karm-ink-muted)" }}
                  >
                    {v} <X className="h-3 w-3" />
                  </button>
                ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mb-2 flex-wrap" data-testid="jobs-meta">
            <p className="small-caps">
              {total === 0
                ? "No roles"
                : `Showing ${((page - 1) * pageSize) + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()} roles`}
            </p>
            <div className="flex items-center gap-2 text-[0.72rem]" data-testid="jobs-page-size-row">
              <span className="small-caps" style={{ letterSpacing: "0.18em" }}>Per page</span>
              {[10, 25, 50].map((n) => (
                <button
                  key={n}
                  onClick={() => changePageSize(n)}
                  className="px-2 py-1 border transition-colors"
                  style={{
                    borderColor: "var(--karm-paper-edge)",
                    color: pageSize === n ? "var(--karm-vermillion)" : "var(--karm-ink-muted)",
                    background: pageSize === n ? "var(--karm-ivory-warm)" : "transparent",
                    fontWeight: pageSize === n ? 600 : 400,
                  }}
                  data-testid={`jobs-page-size-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-24 text-center font-serif italic text-base" style={{ color: "var(--karm-ink-muted)" }} data-testid="jobs-loading">
              ⸻ Loading the press ⸻
            </div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center font-serif italic text-base" style={{ color: "var(--karm-ink-muted)" }} data-testid="jobs-empty">
              ⸻ No roles match your filters. Try widening the search. ⸻
            </div>
          ) : (
            <>
              <div className="-mx-2">
                {items.map((j) => (
                  <JobRow
                    key={j.job_id}
                    job={j}
                    saved={savedSet.has(j.job_id)}
                    onSave={onSave}
                  />
                ))}
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div
                  className="mt-8 pt-6 border-t flex items-center justify-between gap-4 flex-wrap"
                  style={{ borderColor: "var(--karm-paper-edge)" }}
                  data-testid="jobs-pagination"
                >
                  <p className="font-serif italic text-sm" style={{ color: "var(--karm-ink-muted)" }}>
                    Page <span data-testid="jobs-current-page">{page}</span> of {totalPages.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => goToPage(1)}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink)" }}
                      data-testid="jobs-page-first"
                      aria-label="First page"
                    >
                      « First
                    </button>
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink)" }}
                      data-testid="jobs-page-prev"
                      aria-label="Previous page"
                    >
                      ‹ Prev
                    </button>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-3 py-1.5 text-xs border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink)" }}
                      data-testid="jobs-page-next"
                      aria-label="Next page"
                    >
                      Next ›
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={page === totalPages}
                      className="px-3 py-1.5 text-xs border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink)" }}
                      data-testid="jobs-page-last"
                      aria-label="Last page"
                    >
                      Last »
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* End-of-list internal ad */}
          <div className="mt-8">
            <InternalAd slot="jobs_footer" />
          </div>
        </main>

        {/* RIGHT — utility rail */}
        <aside
          className="hidden xl:block border-l p-6 h-[calc(100vh-73px)] sticky top-[73px] overflow-y-auto"
          style={{ borderColor: "var(--karm-paper-edge)" }}
          data-testid="utility-rail"
        >
          <div className="mb-6">
            <InternalAd slot="jobs_sidebar_top" variant="compact" />
          </div>
          <div className="mb-10">
            <p className="small-caps mb-3">Your saved searches</p>
            <p className="font-serif italic text-sm" style={{ color: "var(--karm-ink-muted)" }}>
              {savedSet.size > 0 ? `${savedSet.size} role${savedSet.size > 1 ? "s" : ""} saved.` : "Nothing saved yet. Tap the bookmark on any role."}
            </p>
          </div>

          <div>
            <p className="small-caps mb-3">Featured GCC</p>
            <p className="font-serif text-lg leading-snug mb-1" style={{ color: "var(--karm-ink)" }}>
              HSBC
            </p>
            <p className="font-sans text-xs mb-3" style={{ color: "var(--karm-ink-muted)" }}>
              Open roles worldwide — India, UK, Singapore & more
            </p>
            <Link
              to="/jobs?company=HSBC"
              className="font-serif italic text-sm hover:underline underline-offset-4"
              style={{ color: "var(--karm-vermillion)" }}
              data-testid="featured-hsbc"
            >
              Browse HSBC roles →
            </Link>
          </div>
        </aside>
      </div>

      {/* Mobile filter bottom sheet */}
      {mobileFiltersOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-filter-sheet">
          <div className="absolute inset-0" style={{ background: "rgba(26, 18, 8, 0.4)" }} onClick={() => setMobileFiltersOpen(false)} />
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto p-6 border-t"
            style={{ background: "var(--karm-ivory)", borderColor: "var(--karm-paper-edge)" }}
          >
            <div className="flex items-center justify-between mb-6">
              <p className="font-serif italic text-xl" style={{ color: "var(--karm-ink)" }}>
                Filters
              </p>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="small-caps"
                style={{ color: "var(--karm-vermillion)" }}
                data-testid="mobile-filter-close"
              >
                Close <X className="h-3 w-3 inline" />
              </button>
            </div>
            {FilterRail}
          </div>
        </div>
      )}
    </div>
  );
}
